import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type {
  ChapterRow,
  ChaptersResponse,
  MeResponse,
  PortraitCardKind,
  PortraitResponse,
} from '../../contracts/api'
import { Bases } from '../components/Bases'
import { Collapsible } from '../components/Collapsible'
import { SEAL_GLYPHS, Seal } from '../components/Seal'
import { useNav } from '../nav'
import { haptic } from '../telegram'
import './me.css'

/** Цвет полосы слева у карточки: согласие — зелёное, спор — бордовое. */
const ACCENT: Record<PortraitCardKind, string> = {
  support: 'var(--green)',
  contradiction: 'var(--bordo)',
  condition: 'var(--ochre)',
  unclear: 'var(--text3)',
}

/**
 * «Я» — местность: врождённая структура. Хаб, а не длинная лента: сверху
 * печать и формула, дальше строки-входы (портрет, пересечения, символы,
 * расчёты, журнал, доступ). Всё, что про время, живёт в «Сегодня» и «Циклах».
 *
 * Портрет собирает модель, и он умеет не собраться (нет доступа к модели, 503).
 * Это не повод гасить весь экран: строки хаба — единственный путь к символам,
 * пересечениям и журналу, и они рисуются всегда, даже пока портрета нет.
 */
export function Me(_props: { me: MeResponse }) {
  const [data, setData] = useState<PortraitResponse | null>(null)
  const [chapters, setChapters] = useState<ChapterRow[]>([])
  const [error, setError] = useState('')
  const { navigate } = useNav()

  useEffect(() => {
    let cancelled = false
    api
      .get<PortraitResponse>('/api/portrait')
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'Портрет пока не собрался')
      })
    api
      .get<ChaptersResponse>('/api/chapters')
      .then((result) => {
        if (!cancelled) setChapters(result.chapters)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const open = chapters.filter((chapter) => !chapter.sealed).length

  return (
    <div className="screen me">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">
          открыто {open || 2} из {chapters.length || 8} страниц
        </div>
      </div>

      {data ? (
        <>
          <div className="me-head">
            <Seal
              size={96}
              variant="small"
              coreGlyph={data.seal.coreGlyph ?? undefined}
              slots={data.seal.slots
                .filter((slot) => !slot.sealed)
                .map((slot) => ({
                  id: slot.id,
                  label: slot.label,
                  color: slot.color,
                  sealed: slot.sealed,
                  glyph: SEAL_GLYPHS[slot.id](slot.color),
                }))}
            />
            <div className="card formula-card">
              <div className="formula">
                <div className="f">
                  <div className="lbl">Внутри</div>
                  <div>{data.formula.inside}</div>
                </div>
                <div className="f">
                  <div className="lbl">В действии</div>
                  <div>{data.formula.action}</div>
                </div>
                <div className="f">
                  <div className="lbl">Сейчас учится</div>
                  <div>{data.formula.learning}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="chips coords">
            {data.coordinates.map((coordinate) => (
              <span className="chip" key={coordinate.label}>
                <span className="dot" style={{ background: coordinate.color }} />
                {coordinate.label}
                {coordinate.glyph && (
                  <span className="cjk" style={{ color: coordinate.color }}>
                    &nbsp;{coordinate.glyph}
                  </span>
                )}
              </span>
            ))}
          </div>

          {data.cards.map((card) => (
            <div
              className="card accent-card"
              key={card.title + card.text.slice(0, 20)}
              style={{ boxShadow: `inset 3px 0 0 ${ACCENT[card.kind]}` }}
            >
              <div className="lbl" style={{ color: ACCENT[card.kind] }}>
                {card.title}
              </div>
              <p>{card.text}</p>
              <Bases items={card.bases} />
            </div>
          ))}

          {data.limitations.map((limitation) => (
            <div className="card accent-card limitation-card" key={limitation.code}>
              <div className="lbl">Пока не построено</div>
              <p>{limitation.message}</p>
            </div>
          ))}
        </>
      ) : (
        <p className="me-portrait-off">
          {error ? error : 'Читаем твои карты…'}
        </p>
      )}

      <div className="lbl sect">Твоя карта</div>
      <div className="rowlist me-hub">
        <Collapsible title="Портрет" note={`главы · открыто ${open || 0} из ${chapters.length || 8}`}>
          <div className="rowlist">
            {chapters.map((chapter) => (
              <button
                type="button"
                className={`rowitem${chapter.sealed ? ' sealed' : ''}`}
                key={chapter.id}
                disabled={chapter.sealed}
                onClick={() => {
                  haptic('light')
                  navigate({ screen: { kind: 'chapter', id: chapter.id, title: chapter.title } })
                }}
              >
                <div>
                  <div className="ttl">{chapter.title}</div>
                  <div className="note">{chapter.sealed ? 'запечатано' : chapter.note}</div>
                </div>
                <div className="right">
                  {chapter.sealed ? (
                    <svg width="15" height="15" viewBox="0 0 15 15">
                      <rect x="3" y="6.5" width="9" height="6" rx="1.5" fill="none" stroke="#55535f" strokeWidth="1.3" />
                      <path d="M5 6.5V5a2.5 2.5 0 0 1 5 0v1.5" fill="none" stroke="#55535f" strokeWidth="1.3" />
                    </svg>
                  ) : (
                    '›'
                  )}
                </div>
              </button>
            ))}
            {chapters.length === 0 && <div className="note">Главы ещё собираются.</div>}
          </div>
        </Collapsible>

        <HubRow
          title="Пересечения"
          note="где системы говорят об одном и где спорят"
          onClick={() => navigate({ screen: { kind: 'crossings' } })}
        />
        <HubRow
          title="Символы"
          note="узкая колода: только то, что есть в твоих картах"
          onClick={() => navigate({ screen: { kind: 'symbols' } })}
        />
        <HubRow
          title="Расчёты"
          note="Ба Цзы, Цзы Вэй, Цолькин — карты целиком"
          onClick={() => navigate({ screen: { kind: 'rooms' } })}
        />
        <HubRow
          title="Журнал"
          note="отозвалось или нет — и что из этого повторяется"
          onClick={() => navigate({ screen: { kind: 'journal' } })}
        />
        <HubRow
          title="Доступ и уведомления"
          note="что открыто и что можно открыть"
          onClick={() => navigate({ screen: { kind: 'access' } })}
        />
      </div>
    </div>
  )
}

function HubRow({ title, note, onClick }: { title: string; note: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="rowitem"
      onClick={() => {
        haptic('light')
        onClick()
      }}
    >
      <div>
        <div className="ttl">{title}</div>
        <div className="note">{note}</div>
      </div>
      <div className="right">›</div>
    </button>
  )
}
